import { markSurveillanceRouteListFresh } from './surveillanceRouteListFreshness';

/**
 * Refresco EN SEGUNDO PLANO de la lista de recorridos.
 *
 * ── El fallo que existe para impedir ───────────────────────────────────────
 *
 * El marcador de caducidad (`surveillanceRouteListFreshness`) hacía que el
 * loader pidiera la lista `network-only` cuando una mutación del cronómetro la
 * caducaba. Eso es correcto con red, pero con **cobertura degradada** —la raya
 * en la que la app se cree online y los datos no pasan— la petición ni
 * responde ni falla: el loader se queda esperando y la tabla se queda en el
 * spinner, teniendo las filas en la caché. En carretera eso es lo normal justo
 * después de tocar un tramo, que es cuando la lista queda caducada.
 *
 * ── La regla ───────────────────────────────────────────────────────────────
 *
 * Si hay lista en caché, se sirve YA y el refresco se hace por aquí, sin que
 * nadie lo espere. Cuando llega, se avisa por evento y el listado revalida.
 *
 * 🔴 Se marca al día SOLO si la lectura llegó DE VERDAD al servidor. Sin
 * cobertura, Apollo convierte el `network-only` en `cache-first` y resuelve sin
 * lanzar: darlo por refrescado dejaría la lista sin reintento al recuperar la
 * señal. Y se marca con la versión capturada ANTES de pedir, para que una
 * mutación que caduque la lista mientras la petición está en vuelo no quede
 * tapada por una respuesta que no la incluye.
 */
export const SURVEILLANCE_ROUTE_LIST_REFRESHED_EVENT = 'surveillance-route-list-refreshed';

/**
 * Plazo tras el cual el hueco «hay uno en vuelo» se suelta, aunque la petición
 * siga sin responder.
 *
 * 🔴 Sin esto, un refresco COLGADO —que es el caso para el que existe todo
 * esto— bloqueaba a todos los siguientes para siempre: al volver la cobertura,
 * la lista ya no se reintentaba, que es justo la garantía que promete el
 * marcador de caducidad. La petición vieja no se aborta (`RetryLink` la
 * reintentaría igual); simplemente deja de tapar a las que vengan. Mismo orden
 * de magnitud que el techo de espera de la cola de mutaciones.
 */
export const REFRESH_SLOT_TIMEOUT_MS = 25_000;

type RefreshOptions = {
  /** Quién pide el refresco, para el marcador de caducidad. */
  consumer: string;
  /**
   * Qué lista se está refrescando (los filtros con los que Apollo la
   * particiona). Dos combinaciones de filtros son dos listas distintas, así que
   * el refresco de una no puede dar por servida a la otra: sin esto, cambiar de
   * filtro mientras un refresco está colgado dejaba la lista nueva sin pedir.
   */
  key?: string;
  /** Versión de caducidad vigente ANTES de lanzar la petición. */
  versionBeforeFetch: number;
  /** ¿La lectura salió del dispositivo? (`!client.prioritizeCacheValues`). */
  reachedServer: () => boolean;
  /** Solo para tests: acorta el plazo del hueco en vuelo. */
  slotTimeoutMs?: number;
};

/**
 * Refrescos en vuelo, por lista. Dos navegaciones seguidas a la MISMA lista no
 * disparan dos peticiones: la segunda se engancha a la primera.
 */
const inFlightByKey = new Map<string, Promise<void>>();

export function refreshSurveillanceRouteListInBackground(
  run: () => Promise<unknown>,
  {
    consumer,
    versionBeforeFetch,
    reachedServer,
    key = '',
    slotTimeoutMs = REFRESH_SLOT_TIMEOUT_MS,
  }: RefreshOptions,
): Promise<void> {
  const slot = `${consumer}|${key}`;
  const running = inFlightByKey.get(slot);
  if (running) return running;

  let deadline: ReturnType<typeof setTimeout> | undefined;
  /** Suelta el hueco, pero solo si sigue siendo el de ESTE refresco. */
  const releaseSlot = (): void => {
    if (inFlightByKey.get(slot) === refresh) inFlightByKey.delete(slot);
  };

  const refresh = run()
    .then(() => {
      if (!reachedServer()) return;
      markSurveillanceRouteListFresh(consumer, versionBeforeFetch);
      window.dispatchEvent(new Event(SURVEILLANCE_ROUTE_LIST_REFRESHED_EVENT));
    })
    .catch((error) => {
      // Se conserva lo cacheado y el marcador NO se toca: la lista sigue
      // caducada, así que el próximo intento volverá a pedirla.
      console.log(
        '[SR listado] refresco en segundo plano fallido: se conserva lo cacheado',
        error,
      );
    })
    .finally(() => {
      if (deadline) clearTimeout(deadline);
      releaseSlot();
    });

  deadline = setTimeout(releaseSlot, slotTimeoutMs);
  inFlightByKey.set(slot, refresh);
  return refresh;
}

/** Solo para tests: olvida los refrescos en vuelo. */
export function resetSurveillanceRouteListRefreshForTests(): void {
  inFlightByKey.clear();
}
